// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { QueryClient } from '@tanstack/react-query';

interface InvalidateInvoiceQueriesOptions {
  queryClient: QueryClient;
  invoiceKey: string;
  /**
   * When true, also invalidate the charges listing. Use this for mutations
   * that rebind charges (line-item create/delete, void, draft delete) — the
   * change flips charge status between BILLABLE and INVOICED.
   */
  includeCharges?: boolean;
}

/**
 * Centralized cache invalidation for mutations that modify invoice state.
 *
 * - `['invoices']` is a prefix match that covers BOTH the list query
 *   (`['invoices', 'list', ...]`) and every detail query
 *   (`['invoices', 'detail', <key>]`), so a single call refreshes any
 *   invoice-backed view in the app.
 * - `['invoice', invoiceKey, 'audit']` refreshes the per-invoice audit
 *   timeline — every invoice mutation emits at least one audit event, so
 *   the drawer would otherwise show stale history until a manual refetch.
 * - `['billing-summary']` refreshes the dashboard totals (open, outstanding,
 *   overdue, etc.) since any invoice edit can shift those aggregates.
 */
export function invalidateInvoiceQueries(
  options: InvalidateInvoiceQueriesOptions
): Promise<void> {
  const { queryClient, invoiceKey, includeCharges } = options;
  const invalidations = [
    queryClient.invalidateQueries({ queryKey: ['invoices'] }),
    queryClient.invalidateQueries({
      queryKey: ['invoice', invoiceKey, 'audit'],
    }),
    queryClient.invalidateQueries({ queryKey: ['billing-summary'] }),
  ];
  if (includeCharges) {
    invalidations.push(
      queryClient.invalidateQueries({ queryKey: ['charges'] })
    );
  }
  return Promise.all(invalidations).then(() => undefined);
}
